import type { LeagueSummary } from "@softmax/observatory-client/types";
import { buildHref, type GameDestinationContext } from "@softmax/web-addresses";
import { type WatchAddresses, watchAddresses } from "./league-address-book";

// Explicit ID, path, remembered selection, then corpus default: shared by server and client.
// A stale explicit address uses the neutral default, never a viewer's remembered game.
export function resolveWatchLeague<T extends LeagueSummary>({
  leagues,
  addresses,
  address,
}: {
  leagues: T[];
  addresses: WatchAddresses<T>;
  address: {
    slug?: string | null;
    leagueId?: string | null;
    stored?: string | null;
  };
}): T | null {
  const named = address.leagueId
    ? (leagues.find((league) => league.id === address.leagueId) ?? null)
    : null;

  if (named) return named;

  const onPath = address.slug ? addresses.leagueOf(address.slug) : null;

  if (onPath) return onPath;

  const addressed = Boolean(address.leagueId) || Boolean(address.slug);
  const stored =
    !addressed && address.stored
      ? (leagues.find((league) => league.id === address.stored) ?? null)
      : null;
  return (
    stored ??
    leagues.find(
      (league) => league.game.game_address?.catalog_status !== "unlisted",
    ) ??
    null
  );
}

export function watchPath({
  route,
  params,
}: {
  route: GameDestinationContext | null;
  params: {
    search?: URLSearchParams;
    league?: string | null;
    view?: string | null;
    e?: string | null;
    kiosk?: string | null;
  };
}): string {
  if (params.view === "lobby") return buildHref({ name: "browse" });

  const query = new URLSearchParams(params.search);

  for (const key of ["league", "view", "e", "kiosk"] as const) {
    const value = params[key];

    if (value === null) query.delete(key);
    else if (value !== undefined) query.set(key, value);
  }

  if (!route) return buildHref({ name: "watch", search: query });

  return route.league
    ? buildHref({
        name: "game.league",
        ...route,
        league: route.league,
        search: query,
      })
    : buildHref({ name: "game.home", ...route, search: query });
}

// Public addresses must identify the same league for visitors and owners.
export function theaterCorpusFor({
  slug,
  publicCorpus,
  viewerCorpus,
}: {
  slug: string;
  publicCorpus: LeagueSummary[] | null;
  viewerCorpus: LeagueSummary[] | null;
}): {
  leagues: LeagueSummary[] | null;
  viewerOnly: boolean;
} {
  if (
    publicCorpus &&
    namesLeagueExactly({ addresses: watchAddresses(publicCorpus), slug })
  ) {
    return { leagues: publicCorpus, viewerOnly: false };
  }

  if (
    viewerCorpus &&
    namesLeagueExactly({ addresses: watchAddresses(viewerCorpus), slug })
  ) {
    return { leagues: viewerCorpus, viewerOnly: true };
  }

  return { leagues: publicCorpus, viewerOnly: false };
}

export function namesLeagueExactly({
  addresses,
  slug,
}: {
  addresses: WatchAddresses;
  slug: string;
}): boolean {
  return addresses.exactLeagueOf(slug) !== null;
}
